import { partnerProUntilGrantsPremiumAccess } from './subscriptionEntitlement';

export const DEFAULT_ACCOUNT_SUBSCRIPTION = {
  plan: 'free',
  status: 'active',
  trialEndsAt: null,
  currentPeriodEnd: null,
  partnerProUntil: null,
  partnerName: null,
};

/**
 * User-facing partner label for Settings and subscription copy.
 * @param {string|null|undefined} partnerName
 * @param {string|null|undefined} partnerCode
 */
export function formatPartnerDisplayLabel(partnerName, partnerCode) {
  const name = typeof partnerName === 'string' ? partnerName.trim() : '';
  if (name) return name;
  const code = typeof partnerCode === 'string' ? partnerCode.trim() : '';
  if (code) return code;
  return 'Partner Access';
}

/**
 * Days until partner access ends (0 on last day, 0 once expired).
 * @param {Date} partnerProUntil
 * @param {Date} [now]
 */
export function partnerAccessDaysRemaining(partnerProUntil, now = new Date()) {
  if (!partnerProUntil) return null;
  const diff = partnerProUntil.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

/**
 * Whether to show the pre-expiry upgrade banner in App shell.
 */
/**
 * Settings subscription-card CTA for partner-access users.
 * Hidden until the final 14 days — urgency is handled by PartnerAccessBanner too.
 * @returns {{ label: string, variant: 'filled-primary' } | null}
 */
export function getPartnerSubscriptionPlanAction(partnerProUntil, now = new Date()) {
  if (!partnerProUntil) return null;
  const diffMs = partnerProUntil.getTime() - now.getTime();
  if (diffMs <= 0) return null;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days > 14) return null;
  return { label: 'Subscribe to keep Pro', variant: 'filled-primary' };
}

export function navigateToSubscriptionPage() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('navigateToSubscription'));
  }
}

export function shouldShowPartnerAccessBanner(
  {
    isAnonymousGuest = false,
    hasPaidSubscription = false,
    partnerBannerDismissed = false,
    partnerProUntil = null,
  },
  now = new Date()
) {
  if (isAnonymousGuest || hasPaidSubscription || partnerBannerDismissed || !partnerProUntil) {
    return false;
  }
  const diffMs = partnerProUntil.getTime() - now.getTime();
  if (diffMs <= 0) {
    return false;
  }
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return days <= 14;
}

/**
 * Maps premium hook state to Settings subscription card state.
 */
export function mapPremiumStatusToAccountSubscription({
  isPremium,
  hasPaidSubscription,
  planType,
  subscriptionData,
  partnerProUntil,
  partnerName,
  partnerCode,
}) {
  if (hasPaidSubscription) {
    const periodEnd = subscriptionData?.currentPeriodEnd ?? null;
    const rawStatus = subscriptionData?.status;
    const isTrialing = rawStatus === 'trialing';
    const isCanceled = rawStatus === 'canceled' || subscriptionData?.cancelAtPeriodEnd;

    return {
      plan: isTrialing ? 'trial' : (planType || 'monthly'),
      status: isCanceled ? 'canceled' : (rawStatus === 'past_due' ? 'past_due' : 'active'),
      trialEndsAt: isTrialing ? periodEnd : null,
      currentPeriodEnd: periodEnd,
      partnerProUntil: null,
    };
  }

  if (isPremium && partnerProUntil) {
    return {
      plan: 'partner',
      status: 'active',
      trialEndsAt: null,
      currentPeriodEnd: null,
      partnerProUntil,
      partnerName: formatPartnerDisplayLabel(partnerName, partnerCode),
    };
  }

  return DEFAULT_ACCOUNT_SUBSCRIPTION;
}

/**
 * Whether the partner redeem field should appear under the subscription card.
 */
export function shouldShowPartnerRedeemField(subscriptionPlan) {
  return subscriptionPlan === 'free';
}

/**
 * Derives hook-visible partner access from raw Firestore user fields.
 * Mirrors useEnhancedPremiumStatus partner branch.
 */
export function resolveActivePartnerAccess(partnerProUntilRaw, now = new Date()) {
  if (!partnerProUntilGrantsPremiumAccess(partnerProUntilRaw, now)) {
    return { partnerActive: false, partnerProUntil: null };
  }
  const end =
    partnerProUntilRaw instanceof Date
      ? partnerProUntilRaw
      : partnerProUntilRaw?.toDate?.() ?? new Date(partnerProUntilRaw);
  return { partnerActive: true, partnerProUntil: end };
}
